import { useRef, useState, type FormEvent } from 'react'
import { CheckCircle2, X } from 'lucide-react'

import { Button } from '../../../ui/Button/Button'
import { ApiError } from '../../../services/api/client'
import {
  executeStartConversation,
  type ContactDirectoryRepository,
} from '../../conversations/contact-directory-repository'
import type { PatientListItem } from '../patients-model'
import styles from '../PatientsPage.module.css'
import { usePatientDialog } from '../use-patient-dialog'

function idempotencyKey(prefix: string) {
  const random = globalThis.crypto?.randomUUID?.()
  return random ? `${prefix}-${random}` : `${prefix}-${Date.now()}-fallback`
}

// The start command's own state is unknown. No staff reconcile action exists,
// so the copy points at the conversation, where the result shows.
const unconfirmedCopy =
  'Flio couldn’t confirm whether this message was accepted. Check this patient’s conversation before sending again.'

const failureCopy: Record<string, string> = {
  CONTACT_SELECTION_NOT_FOUND:
    'This patient contact is no longer available. Close this window and refresh Patients.',
  CONTACT_SELECTION_INVALID:
    'This patient contact could not be verified safely.',
  CONTACT_SELECTION_EXPIRED:
    'This contact authorization expired. Close this window and try again.',
  CONTACT_SELECTION_STALE:
    'This patient contact changed. Close this window and refresh Patients.',
  CONTACT_AUTHORITY_CONFLICT:
    'This patient contact could not be resolved safely.',
  PATIENT_CONTACT_AUTHORITY_CONFLICT:
    'This patient record changed and could not be resolved safely.',
  PATIENT_CONTACT_MATERIALIZATION_UNAVAILABLE:
    'Patient messaging is temporarily unavailable. No message was accepted.',
  OPTED_OUT: 'This patient has opted out of SMS messages.',
  ROUTING_NOT_READY: 'SMS is not currently available for this clinic.',
  RECONCILIATION_REQUIRED: unconfirmedCopy,
  IDEMPOTENCY_CONFLICT:
    'This send request conflicted. Close this window before starting another message.',
}

function errorCopy(error: unknown) {
  if (error instanceof ApiError && error.reason) {
    return (
      failureCopy[error.reason] ?? 'The message was not accepted. Try again.'
    )
  }
  if (error instanceof Error) {
    return (
      failureCopy[error.message] ?? 'The message was not accepted. Try again.'
    )
  }
  return 'The message was not accepted. Try again.'
}

export function PatientSmsDialog({
  tenantId,
  patient,
  repository,
  onClose,
  onAccepted,
}: {
  tenantId: string
  patient: PatientListItem
  repository: ContactDirectoryRepository
  onClose: () => void
  onAccepted: (aggregateId: string) => void
}) {
  const selectionKey = useRef<string | null>(null)
  const startKey = useRef<string | null>(null)
  const submissionStarted = useRef(false)
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'accepted' | 'reconciliation'
  >('idle')
  const [error, setError] = useState<string | null>(null)
  const capability = patient.smsContactCapability
  const dialog = useRef<HTMLDivElement>(null)
  usePatientDialog(dialog, { onClose, locked: status === 'sending' })

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const content = message.trim()
    if (
      submissionStarted.current ||
      !content ||
      content.length > 1600 ||
      !capability?.available
    )
      return
    submissionStarted.current = true
    setStatus('sending')
    setError(null)
    selectionKey.current ??= idempotencyKey('patient-contact-selection')
    startKey.current ??= idempotencyKey('patient-start-conversation')
    try {
      const selection = await repository.select(
        tenantId,
        capability.contactRef,
        selectionKey.current
      )
      if (
        selection.contactRef !== capability.contactRef ||
        ![
          'EXISTING_INBOUND_SMS_RELATIONSHIP',
          'EXPLICIT_STAFF_PATIENT_FIRST_CONTACT',
        ].includes(selection.evidence.consentEvidenceState)
      ) {
        throw new Error('CONTACT_SELECTION_STALE')
      }
      const result = await executeStartConversation(
        repository,
        tenantId,
        selection.contactSelectionRef,
        startKey.current,
        content
      )
      if (result.status === 'ACCEPTED') {
        setStatus('accepted')
        onAccepted(result.aggregateId)
        return
      }
      if (
        result.status === 'RECONCILIATION_REQUIRED' ||
        result.status === 'IN_PROGRESS'
      ) {
        setStatus('reconciliation')
        setError(
          result.status === 'IN_PROGRESS'
            ? 'The message is still being processed. Do not resend yet.'
            : unconfirmedCopy
        )
        return
      }
      setStatus('idle')
      setError('The message was not accepted. No success was recorded.')
    } catch (caught) {
      setStatus('idle')
      setError(errorCopy(caught))
    } finally {
      submissionStarted.current = false
    }
  }

  return (
    <div className={styles.modalBackdrop}>
      <div
        ref={dialog}
        className={`${styles.modal} ${styles.smsModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="patient-sms-title"
      >
        <header className={styles.modalHeader}>
          <div>
            <span className={styles.eyebrow}>Patient SMS</span>
            <h2 id="patient-sms-title">Message {patient.displayName}</h2>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            aria-label="Close patient message"
            disabled={status === 'sending'}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        {status === 'accepted' ? (
          <div className={styles.smsAccepted} role="status">
            <CheckCircle2 size={22} aria-hidden="true" />
            <div>
              <strong>Message accepted</strong>
              <p>
                Flio accepted the text for delivery. It will appear in this
                patient's conversation in Conversations shortly.
              </p>
            </div>
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : (
          <form
            className={styles.smsForm}
            onSubmit={(event) => void send(event)}
          >
            <div className={styles.smsRecipient}>
              <span>To</span>
              <strong>{patient.phone}</strong>
              <small>Recipient is fixed from this patient record.</small>
            </div>
            <label htmlFor="patient-sms-message">
              Message
              <textarea
                id="patient-sms-message"
                value={message}
                maxLength={1600}
                rows={5}
                autoFocus
                data-autofocus
                disabled={status !== 'idle'}
                onChange={(event) => {
                  setMessage(event.target.value)
                  setError(null)
                }}
              />
            </label>
            <div className={styles.smsCharacterCount}>
              {message.length}/1600
            </div>
            {error ? (
              <p className={styles.formError} role="alert">
                {error}
              </p>
            ) : null}
            <footer className={styles.modalActions}>
              <Button
                type="button"
                variant="ghost"
                disabled={status === 'sending'}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !message.trim() ||
                  status === 'sending' ||
                  status === 'reconciliation'
                }
              >
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </Button>
            </footer>
          </form>
        )}
      </div>
    </div>
  )
}
